import { isEqual } from "lodash-es";
import { Check, ChevronDown, Info, Plus, Trash2, X } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router";

import useCurrentClusterSupportsSsh from "../hooks/use-current-cluster-supports-ssh";
import CodeSnippet from "./CodeSnippet";
import HelpInfo from "./HelpInfo";
import Quota from "./Quota";
import WizardStep from "./WizardStep";
import { QuestionMarkCircle } from "./icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";
import useQuota from "@/hooks/use-quota";
import { useSettings } from "@/hooks/use-settings";
import { copyToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import { pushError, pushSuccess } from "@/slices/notifications";
import {
  clearForm,
  clearPolicyHash,
  setPolicyFormProperty,
} from "@/slices/policies";
import { setBannerMessage } from "@/slices/pricing_info";
import { useDispatch, useSelector } from "@/store";
import {
  EntityInfo,
  Policy,
  Route,
  useCreatePolicyMutation,
  useCreateRouteMutation,
  useGetClusterQuery,
  useGetSettingsQuery,
  useListCustomDomainsQuery,
  useListDefaultTemplatesQuery,
  useListPoliciesQuery,
  useUpdateSettingsMutation,
} from "@/store/pomeriumApi";
import { createURL } from "@/utils/urls";
import ipPort from "@/validators/ipPort";
import PPLBuilder from "@/views/policies/components/PPLBuilder";

export default function CreateSSHRouteWizard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useSettings();
  const { user } = useContext(UserContext);
  const [wizardStep, setWizardStep] = useState(0);

  const [addRoute] = useCreateRouteMutation();
  const { data: defaultRoute } = useListDefaultTemplatesQuery("route");
  const { clusterId, organizationId } = useSelector((state) => state.current);

  const { data: cluster } = useGetClusterQuery({ organizationId, clusterId });
  const { data: customDomains } = useListCustomDomainsQuery({
    organizationId,
    clusterId,
  });
  const supportsSsh = useCurrentClusterSupportsSsh();

  // redirect back to routes page if SSH not available due to cluster version
  useEffect(() => {
    if (!supportsSsh) {
      navigate("/app/management/routes");
    }
  }, [supportsSsh]);

  // Step 1: Check and set for Cluster SSH Settings
  const [updateSettings] = useUpdateSettingsMutation();
  const { data: clusterSettings } = useGetSettingsQuery({
    organizationId,
    clusterId,
  });
  const { form: clusterSettingsForm } = useSelector((state) => state.settings);

  const [sshAddress, setSshAddress] = useState("");
  const [sshHostKeys, setSshHostKeys] = useState<string[]>([]);
  const [sshUserCaKey, setSshUserCaKey] = useState("");

  useEffect(() => {
    if (clusterSettings?.sshAddress && wizardStep === 0) {
      setWizardStep(1);
    }
  }, [clusterSettings]);

  // Step 2: Create Route - name, to and from URLs
  const [routeName, setRouteName] = useState("");
  const [fromUrl, setFromUrl] = useState("");
  const [toUrl, setToUrl] = useState("");

  const [selectedDns, setSelectedDns] = useState(cluster?.fqdn);

  // Step 3: Apply policies - create SSH specific policies or apply existing
  const [policies, setPolicies] = useState<EntityInfo[]>([]);

  async function handleFinish() {
    try {
      const updatedClusterSettingsForm = {
        ...clusterSettingsForm, // default cluster settings values
        ...clusterSettings, // existing cluster settings
        address: clusterSettingsForm.address || ":443",

        // use existing cluster settings over local form
        sshAddress: clusterSettings?.sshAddress || sshAddress,
        sshHostKeys: clusterSettings?.sshHostKeys || sshHostKeys,
        sshUserCaKey: clusterSettings?.sshUserCaKey || sshUserCaKey,
      };
      await updateSettings({
        organizationId,
        clusterId,
        settingsProperties: updatedClusterSettingsForm,
      });

      const form = {
        ...(defaultRoute && defaultRoute[0].defaultProperties),
        name: routeName,
        from: `ssh://${fromUrl}`,
        to: [`ssh://${toUrl}`],
        policies,
        policyIds: policies.map((p) => p.id),
      };
      const res = await addRoute({
        organizationId,
        routeProperties: { ...form, namespaceId: clusterId } as Route,
      }).unwrap();

      if (res?.id) {
        dispatch(pushSuccess("Route successfully saved!"));
        navigate("/app/management/routes");
      }
    } catch {
      dispatch(pushError("There was a problem saving the route"));
    }
  }

  return (
    <>
      {wizardStep === 0 && !clusterSettings?.sshAddress && (
        <WizardStep
          title="Configure Global SSH Settings"
          subtitle="Create an SSH Route"
          description="Set SSH keys for the cluster"
          onNextStep={() => setWizardStep(1)}
          enableNextStep={
            !!(sshAddress && sshUserCaKey && sshHostKeys.length > 0)
          }
        >
          <Label
            htmlFor="sshAddress"
            className="flex flex-col items-start gap-2"
          >
            <span className="flex gap-1 items-start">
              SSH Address
              <span className="text-destructive">*</span>
              <HelpInfo docsKey="ssh-address" />
            </span>
            <Input
              id="sshAddress"
              type="text"
              placeholder="0.0.0.0:22"
              value={sshAddress}
              validator={ipPort}
              onChange={(e) => setSshAddress(e.currentTarget.value)}
            />
          </Label>

          <Label
            htmlFor="sshHostKeys"
            className="flex flex-col items-start gap-2"
          >
            <span className="flex gap-1 items-center">
              SSH Host Keys
              <span className="text-destructive">*</span>
              <HelpInfo docsKey="ssh-host-keys" />
            </span>
            <ul className="flex flex-col gap-2 w-full">
              {sshHostKeys.map((hostKey, i) => (
                <li key={`hostKey_${i}`} className="w-full flex gap-2">
                  <Textarea
                    id={`hostKey_${i}_input`}
                    className="w-full"
                    value={hostKey}
                    onChange={(e) => {
                      setSshHostKeys(
                        sshHostKeys.with(i, e.currentTarget.value),
                      );
                    }}
                    placeholder={`-----BEGIN OPENSSH PRIVATE KEY-----
          Generated key here
  -----END OPENSSH PRIVATE KEY-----
                `}
                  />
                  <Button
                    variant="destructive"
                    onClick={() => setSshHostKeys(sshHostKeys.toSpliced(i, 1))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              onClick={() => setSshHostKeys([...sshHostKeys, ""])}
              className="w-full"
            >
              <Plus /> Add SSH Host Key
            </Button>
          </Label>

          <Label
            htmlFor="sshUserCaKey"
            className="flex flex-col items-start gap-2"
          >
            <span className="flex gap-1 items-center">
              SSH User CA Key
              <span className="text-destructive">*</span>
              <HelpInfo docsKey="ssh-user-ca-key" />
            </span>
            <Textarea
              id="sshUserCaKey"
              className="w-full"
              value={sshUserCaKey}
              onChange={(e) => setSshUserCaKey(e.currentTarget.value)}
              placeholder={`-----BEGIN OPENSSH PRIVATE KEY-----
        Generated key here
-----END OPENSSH PRIVATE KEY-----
              `}
            />
          </Label>

          <Accordion type="single" collapsible className="my-4">
            <AccordionItem
              value="ssh_hint"
              className="px-4 py-2 !border last:!border border-info rounded-lg"
            >
              <AccordionTrigger>
                <span className="flex gap-2 items-center">
                  <QuestionMarkCircle color="info" aria-hidden="true" />
                  How to generate SSH Keys?
                </span>
              </AccordionTrigger>
              <AccordionContent
                className={cn(
                  "prose prose-sm w-full max-w-none",
                  settings.paletteMode === "dark" && "prose-invert",
                )}
              >
                <h3>Generate User CA Key Pair</h3>
                <p>
                  Generate a key pair that Pomerium will use to sign SSH
                  certificates at your host machine:
                </p>
                <CodeSnippet
                  code='ssh-keygen -N "" -f pomerium_user_ca_key -C "Pomerium User CA"'
                  copyInfo="generateSshCAKeySnippet"
                />
                <p>This creates:</p>
                <ul>
                  <li>
                    <code>pomerium_user_ca_key</code> (private key - to be used
                    in Pomerium)
                  </li>
                  <li>
                    <code>pomerium_user_ca_key.pub</code> (public key -
                    distribute to servers)
                  </li>
                </ul>
                <Separator />
                <h3>Generate SSH Host Keys</h3>
                <p>Generate new SSH host keys for your Pomerium instance:</p>
                <p>
                  This creates three host keys which support different
                  algorithms for compatibility:
                </p>
                <CodeSnippet
                  copyInfo="generateSshHostKeysSnippet"
                  code={`ssh-keygen -N "" -t ed25519 -f pomerium_ssh_host_ed25519_key
ssh-keygen -N "" -t rsa -f pomerium_ssh_host_rsa_key
ssh-keygen -N "" -t ecdsa -f pomerium_ssh_host_ecdsa_key`}
                />
                <ul>
                  <li>
                    <code>pomerium_ssh_host_ed25519_key</code>
                  </li>
                  <li>
                    <code>pomerium_ssh_host_rsa_key</code> (default 3072 bits)
                  </li>
                  <li>
                    <code>pomerium_ssh_host_ecdsa_key</code> (default 256 bits)
                  </li>
                </ul>
                <Button
                  variant="default"
                  className="justify-self-end no-underline"
                  asChild
                >
                  <a
                    href="https://docs.pomerium.com/docs/capabilities/native-ssh-access"
                    target="_blank"
                  >
                    Learn More
                  </a>
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </WizardStep>
      )}

      {wizardStep === 1 && (
        <WizardStep
          title="Configure an SSH Route"
          subtitle="Create an SSH Route"
          description="Define the name, external and destination SSH URLs."
          onPrevStep={
            clusterSettings?.sshAddress ? undefined : () => setWizardStep(0)
          }
          onNextStep={() => setWizardStep(2)}
          enableNextStep={!!(routeName && fromUrl && toUrl)}
        >
          <Label
            htmlFor="routeName"
            className="flex flex-col items-start gap-2"
          >
            <span className="flex gap-1">
              Route Name
              <span className="text-destructive">*</span>
            </span>
            <Input
              id="routeName"
              type="text"
              value={routeName}
              onChange={(e) => setRouteName(e.currentTarget.value)}
            />
          </Label>
          <Label htmlFor="fromUrl" className="flex flex-col items-start gap-2">
            <span className="flex gap-1">
              From URL
              <span className="text-destructive">*</span>
            </span>
            <span className="flex gap-2 w-full items-center">
              <code className="text-neutral-400">ssh://</code>
              <Input
                id="fromUrl"
                type="text"
                value={fromUrl}
                onChange={(e) => setFromUrl(e.currentTarget.value)}
              />
            </span>
          </Label>
          <Label htmlFor="toUrl" className="flex flex-col items-start gap-2">
            <span className="flex gap-1">
              To URL
              <span className="text-destructive">*</span>
            </span>
            <span className="flex gap-2 w-full items-center">
              <code className="text-neutral-400">ssh://</code>
              <Input
                id="toUrl"
                type="text"
                value={toUrl}
                onChange={(e) => setToUrl(e.currentTarget.value)}
              />
            </span>
          </Label>

          {fromUrl && (
            <Alert className="w-full lg:max-w-6xl mx-auto">
              <Info />
              <AlertTitle className="flex flex-col gap-2 mb-2">
                When connecting to this route, use the command:{" "}
                <div className="flex flex-wrap w-fit items-center font-mono tracking-wide border-1 px-2 py-0.25 rounded-lg">
                  <p>
                    ssh user@{fromUrl}@
                    {(!customDomains || customDomains.length === 0) &&
                      `${cluster?.fqdn} -p ${createURL(clusterSettings?.sshAddress || sshAddress).port}`}
                  </p>

                  {customDomains && customDomains.length > 0 && (
                    <>
                      {!(selectedDns === cluster?.fqdn) && <p>ssh.</p>}
                      <Select
                        defaultValue={selectedDns}
                        onValueChange={setSelectedDns}
                      >
                        <SelectTrigger className="ml-1 font-mono py-0 rounded-none border-0 hover:underline">
                          <SelectValue placeholder={cluster?.fqdn} />
                        </SelectTrigger>
                        <SelectContent className="font-mono">
                          <SelectItem key="fqdn" value={cluster?.fqdn || ""}>
                            {cluster!.fqdn}
                          </SelectItem>
                          {customDomains.map((d) => (
                            <SelectItem
                              key={`domain_${d.id}`}
                              value={d.domainName}
                            >
                              {d.domainName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p>
                        {" "}
                        -p{" "}
                        {
                          createURL(clusterSettings?.sshAddress || sshAddress)
                            .port
                        }
                      </p>
                    </>
                  )}
                </div>
              </AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <p className="inline-block">
                  where{" "}
                  <code className="border px-2.5 py-1 rounded-lg">user</code> is
                  the username that would be checked if required by an applied
                  policy.
                </p>
                <Button
                  variant="default"
                  onClick={async () => {
                    await copyToClipboard(
                      `ssh user@${fromUrl}@${selectedDns} -p ${createURL(clusterSettings?.sshAddress || sshAddress).port}`,
                    );
                    sendStandardEvent(
                      `copied_sshPreviewCommand`,
                      "copying",
                      user,
                    );
                    dispatch(pushSuccess("Copied"));
                  }}
                  className="w-fit"
                >
                  Copy SSH Command
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Alert>
            <Info />
            <AlertTitle>Important Information</AlertTitle>
            <AlertDescription>
              Pomerium requires an internet-accessible server reachable on port{" "}
              {createURL(clusterSettings?.sshAddress || sshAddress).port}.
            </AlertDescription>
          </Alert>
        </WizardStep>
      )}

      {wizardStep === 2 && (
        <WizardStep
          title="Create or Apply Existing Policies"
          subtitle="Create an SSH Route"
          description="Secure your route with cluster policies"
          onPrevStep={() => setWizardStep(1)}
          onNextStep={() => setWizardStep(3)}
        >
          <small className="text-sm leading-none font-medium">
            Apply existing Policies:
          </small>
          <PolicyPickerCombobox policies={policies} setPolicies={setPolicies} />
          <Separator className="my-2" />
          <LocalPPLBuilder policies={policies} setPolicies={setPolicies} />
          <Separator />
        </WizardStep>
      )}

      {wizardStep === 3 && (
        <WizardStep
          title="Configure Upstream Server"
          subtitle="Create an SSH Route"
          description="Update your Pomerium instance's machine"
          onPrevStep={() => setWizardStep(2)}
          onFinishStep={handleFinish}
        >
          <p>
            Copy the User CA public key (<code>pomerium_user_ca_key.pub</code>)
            to your upstream servers. It can be placed in any (root-owned)
            location - for example, <code>/etc/pomerium/</code> or{" "}
            <code>/var/lib/pomerium/</code>. You may also place it in{" "}
            <code>/etc/ssh/</code> if desired.
          </p>
          <p>
            Update sshd config by creating a new config file in
            /etc/ssh/sshd_config.d/ (or editing /etc/ssh/sshd_config directly):
          </p>
          <CodeSnippet
            copyInfo="editSshd"
            code={`# Trust Pomerium's User CA for certificate authentication
TrustedUserCAKeys /path/to/pomerium_user_ca_key.pub`}
          />
          <p>And restart the SSH daemon:</p>
          <CodeSnippet
            copyInfo="sshRestartSnippet"
            code={`# Ubuntu/Debian
sudo systemctl restart ssh

# Other Linux distributions
sudo systemctl restart sshd

# Check service status

# Ubuntu/Debian
sudo systemctl status ssh

# Other Linux distributions
sudo systemctl status sshd`}
          />
        </WizardStep>
      )}
    </>
  );
}

// TODO: extract into solo component (MultiSelectCombobox)
function PolicyPickerCombobox({
  policies,
  setPolicies,
}: {
  policies: EntityInfo[];
  setPolicies: Dispatch<SetStateAction<EntityInfo[]>>;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data: result } = useListPoliciesQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
    },
    { skip: !organizationId || !clusterId },
  );

  const policyOptions: EntityInfo[] =
    // @ts-expect-error This is similar to line 45 from `PolicyPicker.tsx`
    // The type errors doesn't exist there, but does here
    // Hence, the `@ts-ignore` to remove any build errors
    result?.rows.map((p) => ({
      id: p.id,
      name: p.name,
      updatedAt: p.updatedAt,
    })) || [];

  function addPolicyById(id: string) {
    const policy = policyOptions.find((p) => p.id === id);
    if (policy) {
      setPolicies([...policies, policy]);
    }
  }

  function removePolicy(policy: EntityInfo) {
    const index = policies.indexOf(policy);
    if (index > -1) {
      setPolicies(policies.toSpliced(index, 1));
    }
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger
        role="combobox"
        aria-expanded={isPopoverOpen}
        className="w-full border rounded-lg px-3 py-2 text-start flex justify-between items-center"
      >
        <>
          <span className="flex gap-2 flex-wrap">
            {policies.length > 0
              ? policies.map((policy) => (
                  <Badge
                    key={policy.id}
                    variant="outline"
                    className="flex gap-2 pl-3 pr-1"
                  >
                    <p>{policy.name}</p>
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        removePolicy(policy);
                      }}
                    >
                      <X />
                    </Button>
                  </Badge>
                ))
              : "Select policies..."}
          </span>
        </>
        <ChevronDown className="size-3 dark:text-muted" />
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No matching policy.</CommandEmpty>
            {policyOptions.map((policy) => (
              <CommandItem
                key={policy.id}
                value={policy.name}
                onSelect={() => {
                  if (!policies.find((p) => isEqual(policy, p))) {
                    addPolicyById(policy.id);
                  }
                  setIsPopoverOpen(false);
                }}
                className="flex justify-between"
              >
                {policy.name}
                {policies.find((p) => isEqual(policy, p)) && (
                  <Check className="size-3" />
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// TODO: extract into solo component and replace Redux component `PPLBuilder`
function LocalPPLBuilder({
  policies,
  setPolicies,
}: {
  policies: EntityInfo[];
  setPolicies: Dispatch<SetStateAction<EntityInfo[]>>;
}) {
  const dispatch = useDispatch();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data: defaultPolicy } = useListDefaultTemplatesQuery("policy");
  const { form } = useSelector((state) => state.policies);
  const { count, limit, refetch: refetchQuota } = useQuota("policies");
  const [addPolicy] = useCreatePolicyMutation();

  async function createAndApplyPolicy() {
    const newPolicy = await addPolicy({
      organizationId,
      policyProperties: {
        ...(defaultPolicy && defaultPolicy[0].defaultProperties),
        ...form,
        namespaceId: clusterId,
      } as Policy,
    });
    if ("error" in newPolicy) {
      dispatch(pushError("Error creating policy"));
    } else {
      setPolicies([
        ...policies,
        {
          id: newPolicy.data.id,
          name: newPolicy.data.name,
          updatedAt: newPolicy.data.updatedAt,
        },
      ]);
      dispatch(clearForm());
      refetchQuota();
    }
  }

  return (
    <>
      <div className="flex w-full justify-between">
        <small className="text-sm leading-none font-medium">
          Create a new policy:
        </small>
        <Quota quota="policies" label="policies" />
      </div>
      {count >= limit ? (
        <div className="flex flex-col gap-2 items-center">
          <p>
            This cluster has reached its policy limit. Upgrade to create more
            security rules for your routes.
          </p>
          <Button
            variant="default"
            onClick={() => dispatch(setBannerMessage(""))}
            className="w-fit"
          >
            See plans
          </Button>
        </div>
      ) : (
        <>
          <section className="flex w-full gap-4">
            <Label
              htmlFor="policyName"
              className="flex flex-col items-start gap-2 w-full"
            >
              <span className="flex gap-1">
                Policy Name
                <span className="text-destructive">*</span>
              </span>
              <Input
                id="policyName"
                type="text"
                value={form?.name}
                onChange={(e) =>
                  dispatch(
                    setPolicyFormProperty({
                      path: "name",
                      value: e.currentTarget.value,
                    }),
                  )
                }
              />
            </Label>
            <Label
              htmlFor="description"
              className="flex flex-col items-start gap-2 w-full"
            >
              Description
              <Input
                id="description"
                type="text"
                value={form?.description}
                onChange={(e) =>
                  dispatch(
                    setPolicyFormProperty({
                      path: "description",
                      value: e.currentTarget.value,
                    }),
                  )
                }
              />
            </Label>
          </section>
          <PPLBuilder readonly={false} />
          <div className="flex gap-4 justify-end">
            <Button
              variant="ghost"
              onClick={() => {
                dispatch(clearForm());
                dispatch(clearPolicyHash());
              }}
            >
              Clear Form
            </Button>
            <Button variant="secondary" onClick={createAndApplyPolicy}>
              Apply Policy
            </Button>
          </div>
        </>
      )}
    </>
  );
}
